import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { equal, empty } from '@ember/object/computed';
import Controller from '@ember/controller';
import { task } from 'ember-concurrency';
import { isEmpty, typeOf } from '@ember/utils';

export default class IndexController extends Controller {
  @service alerts;
  @service api;
  @service customer;
  @service raven;

  isShowingEditor = false;
  isShowingSendConfirmModal = false;
  sendData = null;
  isSendDataValid = true;

  @empty('model.recipientsFilter') isRecipientsFilterEmpty;

  @equal('fetchRecipientCount.last.value', 0) isRecipientCountZero;

  @computed('isRecipientsFilterEmpty', 'isRecipientCountZero', 'fetchRecipientCount.isRunning')
  get sendConfirmModalTitle() {
    let { isRecipientsFilterEmpty, isRecipientCountZero, fetchRecipientCount } = this.getProperties(
      'isRecipientsFilterEmpty',
      'isRecipientCountZero',
      'fetchRecipientCount',
    );

    if (fetchRecipientCount.isRunning) {
      return 'Loading recipients…';
    }

    if (isRecipientsFilterEmpty || isRecipientCountZero) {
      return 'Who should receive this broadcast?';
    }

    return 'Send manually?';
  }

  send = task(async () => {
    let campaignId = this.get('model.id');
    let sendData = this.get('sendData');

    let data = {};

    if (sendData) {
      data.data = sendData;
    }

    try {
      await this.get('api').post(`campaigns/${campaignId}/triggers`, { data });
      this.get('alerts').success('broadcast.manual_send', 'Broadcast triggered');
    } catch (e) {
      this.get('raven').captureException(e);
    } finally {
      this.set('isShowingSendConfirmModal', false);
    }
  });

  fetchRecipientCount = task(async () => {
    let campaign = this.get('model');

    if (isEmpty(campaign.get('recipientsFilter'))) {
      return 0;
    }

    return await this.get('customer.fetchRecipientCount').perform(campaign.get('recipientsFilterQuery'));
  });

  resetEditor() {
    this.setProperties({
      isShowingEditor: false,
      isShowingSendConfirmModal: false,
      sendData: null,
      isSendDataValid: true,
    });
  }

  @action
  selectTriggerId({ target }) {
    target.focus();
    target.select();
  }

  @action
  showSendConfirmModal() {
    if (!this.get('isRecipientsFilterEmpty')) {
      this.get('fetchRecipientCount').perform();
    }
    this.set('isShowingSendConfirmModal', true);
  }

  @action
  showEditor() {
    this.set('isShowingEditor', true);
  }

  @action
  hideEditor() {
    this.resetEditor();
  }

  @action
  onValidate(valid) {
    // We need to validate the send data ourselves even after Ace Editor's
    // checks, because we only accept objects as trigger data, and Ace Editor
    // will return valid for strings or arrays (since, technically, those are
    // valid JSON)
    if (valid) {
      try {
        let parsed = JSON.parse(this.sendData);

        if (typeOf(parsed) !== 'object') {
          this.set('isSendDataValid', false);
          return;
        }
      } catch {
        this.set('isSendDataValid', false);
        return;
      }
    }

    this.set('isSendDataValid', valid);
  }
}
